import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

const STATUSES = [
  { v: 'label_created', label: 'Label created' },
  { v: 'in_transit', label: 'In transit' },
  { v: 'out_for_delivery', label: 'Out for delivery' },
  { v: 'ready_for_pickup', label: 'Ready for pickup' },
  { v: 'delivered', label: 'Delivered' },
  { v: 'exception', label: 'Exception' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.v, s.label]));

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    const res = await apiFetch(`/api/packages/${id}`);
    if (res.ok) { const d = await res.json(); setPkg(d.package); }
    else if (res.status === 404) { setMsg('Package not found.'); }
  }
  useEffect(() => { load(); }, [id]);

  async function addUpdate() {
    if (!note.trim() && !status) { setMsg('Add a note or pick a status.'); return; }
    setBusy(true); setMsg('');
    const res = await apiFetch(`/api/packages/${id}/updates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note, status: status || undefined }),
    });
    setBusy(false);
    if (res.ok) { setNote(''); setStatus(''); load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not save update.'); }
  }

  async function toggleArchive() {
    const archived = !pkg.archived_at;
    const res = await apiFetch(`/api/packages/${id}/archive`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived }),
    });
    if (res.ok) { const d = await res.json(); setPkg({ ...pkg, ...d.package }); }
  }

  async function remove() {
    if (!window.confirm('Delete this package? This cannot be undone.')) return;
    const res = await apiFetch(`/api/packages/${id}`, { method: 'DELETE' });
    if (res.ok) navigate('/packages');
  }

  function fmt(s) { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return '—'; } }

  if (msg && !pkg) return <div className="hq-page-content"><div className="hq-page-head"><h1>Packages</h1><p>{msg}</p></div></div>;
  if (!pkg) return <div className="hq-page-content"><div className="hq-page-head"><p>Loading…</p></div></div>;

  const latest = (pkg.updates || [])[0];

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>{pkg.tracking_number}</h1>
        <p>{(pkg.carrier || '').toUpperCase()} · {pkg.label || 'No label'}</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Current status</h2>
          <span className={`hq-pill hq-pkg-${pkg.status}`}>{STATUS_LABEL[pkg.status] || pkg.status}</span>
        </div>
        <table className="hq-table">
          <tbody>
            <tr><td className="hq-role">Est. delivery</td><td>{pkg.estimated_delivery ? fmt(pkg.estimated_delivery) : '—'}</td></tr>
            {/* @@PKG_ADDR_v57@@ */}
            <tr><td className="hq-role">Ship from</td><td>{pkg.origin || '—'}</td></tr>
            <tr><td className="hq-role">Ship to</td><td>{pkg.destination || '—'}</td></tr>
            <tr><td className="hq-role">Latest update</td><td>{latest ? fmt(latest.created_at) : '—'}</td></tr>
            <tr><td className="hq-role">Latest note</td><td>{latest && latest.note ? latest.note : '—'}</td></tr>
            <tr><td className="hq-role">Total updates</td><td>{pkg.update_count || 0}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Add an update</h2></div>
        <div style={{ padding: 20 }}>
          <div className="hq-form">
            <select className="hq-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Keep current status</option>
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
            <input className="hq-input" placeholder="Note" value={note}
              onChange={(e) => setNote(e.target.value)} style={{ minWidth: 280, flex: 1 }} />
            <button className="hq-btn" onClick={addUpdate} disabled={busy}>{busy ? 'Saving…' : 'Add update'}</button>
          </div>
          {msg && <p className="hq-msg">{msg}</p>}
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>History</h2></div>
        <table className="hq-table">
          <thead><tr><th>When</th><th>Status</th><th>Note</th><th>Source</th></tr></thead>
          <tbody>
            {(pkg.updates || []).map((u) => (
              <tr key={u.id}>
                <td>{fmt(u.created_at)}</td>
                <td>{u.status ? (STATUS_LABEL[u.status] || u.status) : '—'}</td>
                <td>{u.note || '—'}</td>
                <td style={{ color: 'var(--hq-muted)', fontSize: 12, textTransform: 'capitalize' }}>{u.source}</td>
              </tr>
            ))}
            {(!pkg.updates || pkg.updates.length === 0) && (
              <tr><td className="hq-empty" colSpan={4}>No updates recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="hq-signout" style={{ width: 'auto', padding: '0 16px' }} onClick={() => navigate('/packages')}>Back</button>
        <button className="hq-signout" style={{ width: 'auto', padding: '0 16px' }} onClick={toggleArchive}>{pkg.archived_at ? 'Unarchive' : 'Archive'}</button>
        <button className="hq-signout" style={{ width: 'auto', padding: '0 16px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }} onClick={remove}>{pkg.archived_at ? 'Purge package' : 'Delete package'}</button>
      </div>
    </div>
  );
}
